import React from 'react';
import { Box, Skeleton, Paper, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import PageContainer, { PageContainerContext } from '../components/common/PageContainer';
import KpiRow, { KpiCell } from '../components/common/KpiRow';
import { SP } from '../theme/tokens';

/**
 * CompetitorListSkeleton — der Inhalt der Wettbewerber-Liste im Ladezustand.
 *
 * 🔴 Welle 2 PR 3b, Nachbesserung 2 (2026-10-06): der eigene `PageContainer`
 * traegt seinen Rand nur, wenn das Skelett ALLEIN steht. Steht es schon in einem
 * (`CompetitorList.jsx`, Lade-Zweig), addierte der Theme-Override des
 * `MuiContainer` unter `sm` 16 px auf den Rand des aeusseren — der Inhalt saesse
 * 16 px weiter innen als geladen. Dieselbe Bauform wie `ScraperListSkeleton`.
 *
 * 🔴 R16 C1 (2026-10-06): KEINE Breadcrumb-/Kopf-Platzhalter mehr. Den Kopf
 * rendert die Seite in JEDEM Zustand (`CompetitorList.jsx`, `const kopf`).
 *
 * 🔴 G9b (§2.2 Punkt 1/2): das Skelett traegt `data-testid="liste-skelett"` und
 * `width="full"` wie die geladene Seite — sonst springt der Rahmen beim
 * Eintreffen der Daten von `reading` auf `full`. Es zeichnet die NEUE
 * Leistenform (EINE Suche, keine zweite Zeile) und die fuenf Spalten des
 * Hauskopfs, damit die Seite beim Eintreffen nicht springt (R16-B-W1).
 *
 * 🔴 R16 W1 (Nachbesserung 1, 2026-10-22): das KPI-Band fehlte. Der Docstring
 * sagte „damit die Seite beim Eintreffen nicht springt" — und genau das tat sie
 * um die Hoehe des Streifens, weil `CompetitorList.jsx` ihn geladen rendert
 * (`{stats && … <KpiRow>}`) und das Geruest nicht. Gebaut aus DEMSELBEN Baustein
 * wie die geladene Reihe (`KpiRow`/`KpiCell`), Vorbild
 * `VendorsOverviewSkeleton.jsx:54` — vier Kacheln wie die geladenen
 * (Total / Avg targets / Avg products / Avg markets), nicht die fuenf der
 * Schwesterliste. Der Aussenabstand ist `SP.md`: geladen traegt die Reihe
 * `mb: 3` (= 24 px = `SP.md`, `CompetitorList.jsx`).
 */
export default function CompetitorListSkeleton() {
  const verschachtelt = React.useContext(PageContainerContext);

  const inhalt = (
    <Box data-testid="liste-skelett">
      {/* KPI-Streifen — vier Kacheln wie die geladene Reihe, aus `KpiRow` selbst */}
      <KpiRow data-testid="liste-kpi-skelett" sx={{ mb: SP.md }}>
        {[...Array(4)].map((_, i) => (
          <KpiCell key={i}>
            <Skeleton variant="text" width="55%" />
            <Skeleton variant="text" width="40%" sx={{ height: 28 }} />
          </KpiCell>
        ))}
      </KpiRow>

      {/* Search — die eine Leistenzeile (ListToolbar) */}
      <Box mb={2}>
        <Skeleton variant="rectangular" width="100%" height={40} />
      </Box>

      {/* Table — fuenf Spalten wie SORT_FELDER */}
      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              {['Competitor', 'Scraper targets', 'Scraped products', 'Markets', 'Actions'].map((h, i) => (
                <TableCell key={i}>
                  <Skeleton variant="text" width={h.length * 10} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {[...Array(5)].map((_, row) => (
              <TableRow key={row}>
                {[...Array(5)].map((__, cell) => (
                  <TableCell key={cell}>
                    <Skeleton variant="text" width="80%" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );

  return verschachtelt ? inhalt : <PageContainer width="full">{inhalt}</PageContainer>;
}
